export interface EventThemeVars {
  primaryColour?: string | null | undefined; // sidebar background
  secondaryColour?: string | null | undefined; // event header background
  calendarPrimaryColour?: string | null | undefined;
  calendarSecondaryColour?: string | null | undefined;
  fontFamily?: string | null | undefined;
  fontSize?: string | null | undefined; // px value, e.g. "16"
  fontColour?: string | null | undefined;
}

// Uses relative luminance on a #rrggbb value; returns false for anything else
function isLightColour(hex: string) {
  const digits = /^#?([0-9a-f]{6})$/i.exec(hex.trim())?.[1];
  if (!digits) return false;
  const n = parseInt(digits, 16);
  const channel = (shift: number) => {
    const c = ((n >> shift) & 0xff) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(16) + 0.7152 * channel(8) + 0.0722 * channel(0);
  return luminance > 0.4;
}

function setOrRemove(prop: string, value?: string | null) {
  if (value) document.documentElement.style.setProperty(prop, value);
  else document.documentElement.style.removeProperty(prop);
}

// Mirrors the saved event theme onto the CSS variables used across the app.
// Unset values are removed so the stylesheet defaults apply.
export function applyEventThemeVars(theme?: EventThemeVars | null) {
  const sidebar = theme?.primaryColour;
  setOrRemove("--sidebar-bg", sidebar);
  // Hover and active states follow the sidebar colour so no platform default shows through
  setOrRemove(
    "--sidebar-bg-hover",
    sidebar ? `color-mix(in srgb, ${sidebar} 80%, black)` : null,
  );
  // Sidebar text and icons follow the font colour when one is set
  const fontColour = theme?.fontColour;
  setOrRemove("--sidebar-text", fontColour);
  setOrRemove("--sidebar-text-heading", fontColour);
  // Active items sit on a light background, so a light font colour is darkened there
  setOrRemove(
    "--sidebar-text-active",
    fontColour && !isLightColour(fontColour)
      ? fontColour
      : sidebar
        ? `color-mix(in srgb, ${sidebar} 60%, black)`
        : fontColour
          ? "#171717"
          : null,
  );
  setOrRemove("--event-header-bg", theme?.secondaryColour);
  setOrRemove("--calendar-primary", theme?.calendarPrimaryColour);
  setOrRemove("--calendar-secondary", theme?.calendarSecondaryColour);
  setOrRemove("--event-font-colour", theme?.fontColour);

  // Font size sets the root size, so the rem-based Tailwind scale follows it
  setOrRemove("font-size", theme?.fontSize ? `${theme.fontSize}px` : null);
  document.body.style.fontFamily = theme?.fontFamily ?? "";
  document.body.style.color = theme?.fontColour ?? "";
}
